import { Injectable } from '@angular/core';
import { CustomTopic } from './graphql/game.service';
import { Router } from '@angular/router';
import { environment } from '@app/environment';
import { CountryViewModel, LanguageViewModel } from '@app/shared/rest-api';

export interface User {
  jokerSkip?: number;
  jokerFiftyFifty?: number;
  score?: number;
  playInactivityCounter?: number;
  id: string;
  username: string;
  avatar: string;
  location?: UserLocation;
  lastGamePlayed: number;
  highestScore: number;
  activeGame: number;
}

export interface UserLocation {
  country: CountryViewModel;
  language: LanguageViewModel;
}

export interface Session {
  id: string;
  user: User;
  location: UserLocation;
  lastTopic: CustomTopic;
  musicOff: boolean;
  soundsOff: boolean;
  fiftyToken: string;
  skipToken: number[];
}

type StringDict = { [key: string]: string };

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  public session: Session;
  private sessionKey: string;
  private playersKey: string;
  private default_lang: LanguageViewModel;
  private accessCodeKey: string;  

  translations: StringDict | null;

  constructor(private router: Router) {
    this.sessionKey = 'j-sess';
    this.playersKey = 'j-pls';
    this.accessCodeKey = 'j-access_code';
    this.default_lang = {
      culture: 'English (United Kingdom)',
      name: 'English',
      isoCode2Letters: 'en',
      isoCode4Letters: 'en-GB',
    };

    this.initSession();
    this.initPlayersHistory();

    this.translations = null;
  }

  public setAccessCode(code: string): void {
    localStorage.setItem(this.accessCodeKey, code);
  }

  public getAccessCode(): string {
    return localStorage.getItem(this.accessCodeKey);
  }

  public setTranslations(translations: StringDict) {
    this.translations = translations;
  }

  public initSession(){
    const session = this.getSession();
    if(session && session.id ) {
      this.session = session;
      return;
    }

    this.createSession();
  }

  private createSession() {
    this.session = {
      id: new Date().getTime().toString(),
      user: null,
      location: null,
      lastTopic: null,
      musicOff: true,
      soundsOff: true,
      fiftyToken: '',
      skipToken: []
    };

    this.updateSession();
  }

  private initPlayersHistory() {
    const playersHistory = localStorage.getItem(this.playersKey);

    if(!playersHistory) {
      localStorage.setItem(this.playersKey, JSON.stringify([]));
    }
  }

  public setUser(user: User, newUser: boolean) {
    if(newUser) {
      user.location = this.session.location;
    }
    this.session.user = user;

    this.updateSession();
    if(newUser) {
      this.addHistoryProfile(user);
    }
  }

  public getUser(): User {
    return this.session.user;
  }

  public updateUserGameId(gameId: number) {
    let user = this.getUser();
    if(!user){
      return null;
    }

    if(gameId) {
      user.lastGamePlayed = gameId;
    }

    user.activeGame = gameId;
    this.session.user = user;

    this.updateSession();

    return user;
  }

  public updateUserHighestScore(score: number) {
    const user = this.getUser();
    
    if(score > user.highestScore) {
      user.highestScore = score;
      this.session.user = user;
  
      this.updateSession();
    }
   
    return user;
  }

  public updateUserScore(score: number) {
    this.session.user.score = score;

    this.updateSession();
    return this.session;
  }

  public setLocation(location: UserLocation) {
    this.session.location = location;

    this.updateSession();
  }

  public setLastTopic(topic: CustomTopic | null) {
    this.session.lastTopic = topic;
    this.updateSession();
  }

  public getLastTopic() {
    return this.session.lastTopic
  }

  public setMusicOff(val: boolean) {
    this.session.musicOff = val;
    this.updateSession();
  }

  public setSoundsOff(val: boolean) {
    this.session.soundsOff = val;
    this.updateSession();
  }

  public getLocation(): UserLocation {
    return this.session.location;
  }

  public checkCookiesLifeTime = (): void => {  
    const cookieTimer = localStorage.getItem(environment.cookiesTimerKey); 
    if(!cookieTimer){
      this.setCookiesTimer();
      return;
    }

    const today = new Date();  
    if(today.valueOf() >= (JSON.parse(cookieTimer) as number)) {
      localStorage.clear();
      
      this.setCookiesTimer();
      this.initSession();
      this.initPlayersHistory();
      this.router.navigateByUrl('/');
    }
  };

  public setCookiesTimer = (): void => {  
    const today = new Date();
    const cookiesDuration = today.setMonth(today.getMonth() + environment.cookiesExpiryDurationInMonths)  
    const cookieTimer = JSON.stringify(cookiesDuration);    
    localStorage.setItem(environment.cookiesTimerKey, cookieTimer);
  }

  public getSession(): Session | null {
    const session = localStorage.getItem(this.sessionKey);
    return session ? JSON.parse(session) : null;
  }

  private updateSession() {
    const session = JSON.stringify(this.session);
    localStorage.setItem(this.sessionKey, session);
    if (this.session.user) {
      this.updateProfilesHistory(this.session.user);
    }
  }

  public logoutUser() {
    this.session.user = null;
    this.session.location.language = this.default_lang;
    this.session.location.country = null;

    this.updateSession();
  }

  public isUserAuthenticated(): boolean {
    return !!this.getSession()?.user;
  }

  private addHistoryProfile(player: User): void {
    let players = this.getProfilesHistory();
    players.push(player);

    localStorage.setItem(this.playersKey, JSON.stringify(players));
  }

  public getProfilesHistory(): User[] {
    let playersHistory = localStorage.getItem(this.playersKey);
    return playersHistory ? JSON.parse(playersHistory) : [];
  }
  
  private updateProfilesHistory(user: User) {
    let profiles = this.getProfilesHistory();
    const profileIndex = profiles.findIndex(p => p.id === user.id);

    if(profileIndex >= 0) {
      profiles[profileIndex].lastGamePlayed = user.lastGamePlayed;
      profiles[profileIndex].activeGame = user.activeGame;
      profiles[profileIndex].location = user.location;
      profiles[profileIndex].score = user.score;
      profiles[profileIndex].highestScore = user.highestScore;
    }

    localStorage.setItem(this.playersKey, JSON.stringify(profiles));
  }

  public removeProfile(profileId: string) {
    let profiles = this.getProfilesHistory();

    const foundIndex = profiles.findIndex(profile => profile.id === profileId);

    if(foundIndex >=0) {
      profiles.splice(foundIndex, 1);
      localStorage.setItem(this.playersKey, JSON.stringify(profiles));
    }

    return profiles;
  }

  public fiftyTokenUsed(map:string) {
    this.session.fiftyToken = map;
    this.updateSession();
  }

  public skipTokenUsed(roundId: number) {
    this.session.skipToken.push(roundId);
    this.updateSession();
  }

  public clearSession() {
    this.session.fiftyToken = '';
    this.session.skipToken = [];
    this.session.lastTopic = null;

    this.updateSession();
  }
}